import { Directive, HostListener, Input, OnDestroy, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { InputTransformProcess, StyleprofileService, InputTransforms } from '../core/styleprofile.service';

@Directive({
  selector: '[textInputTransform]'
})
export class TextInputTransformDirective implements OnInit, OnDestroy {
  @Input('textInputTransform') textInputTransform: any;
  @Input('codemirrorInstance') codemirrorInstance: any;
  private lastKeyPressed: string | null = null;
  
  private isAfterTransform = false;
  private lastOriginalText: string | null = null;
  private lastReplacementText: string | null = null;
  private lastReplaceCursorPos: number | null = null;
  inputTransformsConfig: InputTransformProcess[];

  constructor(
    private renderer: Renderer2,
    private control: NgControl,
    private styleProfile: StyleprofileService,
  ) { }
  
  private styleProfileSub: Subscription;
  ngOnInit(): void {
    this.styleProfileSub = this.styleProfile.getStyleProfileChanges().subscribe((hasStyleProfile) => {
      if(hasStyleProfile) { this.inputTransformsConfig = this.styleProfile.getTextInputTransforms(); }
    })
    this.initCodeMirrorEventListener();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.codemirrorInstance) {
      this.codemirrorInstance = changes.codemirrorInstance.currentValue;
      this.initCodeMirrorEventListener();
    }
  }
  ngOnDestroy(): void {
    if(this.styleProfileSub) {
      this.styleProfileSub.unsubscribe();
    }
  }
  initCodeMirrorEventListener() {
    if (this.codemirrorInstance) {
      this.codemirrorInstance.cm.on('change', (instance:any, change:any) => this.codeMirrorHandler(instance, change));
    }
  }
  
  @HostListener('blur')
  onBlur(): void {
    this.isAfterTransform = false;
  }
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    this.lastKeyPressed = event.key;
  }
  @HostListener('input', ['$event'])
  onInput(event: InputEvent) {
    if (this.codemirrorInstance) return;
    if (this.lastKeyPressed == null) return;
    this.checkForUndoTransformation(event);
    this.handleTransformations(event, this.inputTransformsConfig);
  }
  codeMirrorHandler(instance: any, change: any) {
    const event = codemirrorChangeToInputEvent(instance, change);
    if (event == undefined) return;
    this.checkForUndoTransformation(event);
    this.handleTransformations(event, this.inputTransformsConfig);
  }
  
  handleTransformations(event: InputEvent, config: InputTransformProcess[]) {
    for (let inputTransform of config) {
      // use switch case, because when using object, the `this` context is lost
      switch (inputTransform.slug) {
        case InputTransforms.CURLY_QUOTES: this.curlyQuotesTransform(event); break;
      }
    }
  }
  
  replaceTextBackwards(target: HTMLInputElement | HTMLTextAreaElement, replacement: string, len: number, cursorPos: number) {
    this.replaceText(target, replacement, cursorPos - len, cursorPos);
  }
  replaceText(target: HTMLInputElement | HTMLTextAreaElement, replacement: string, start: number, end: number) {
    this.lastOriginalText = target.value.slice(start, end);
    this.lastReplacementText = replacement;
    this.lastKeyPressed = null;
    this.lastReplaceCursorPos = start + replacement.length;
      
    if (this.codemirrorInstance) {
      // ignore target, use this.codemirrorInstance
      this.replaceTextCM(replacement, start, end);
    } else {
      this.replaceTextHTML(target, replacement, start, end);
    }
    
    this.isAfterTransform = true;
  }
  replaceTextCM(replacement: string, start: number, end: number) {
    const doc = this.codemirrorInstance;
    const startPos = doc.posFromIndex(start);
    const endPos = doc.posFromIndex(end);
    doc.replaceRange(replacement, startPos, endPos);
  }
  replaceTextHTML(target: HTMLInputElement | HTMLTextAreaElement, replacement: string, start: number, end: number) {
    target.setSelectionRange(start, end);
    // use execCommand to preserve the undo stack
    const success = document.execCommand('insertText', false, replacement);
    // fallback to manual value change if execCommand fails
    if (!success) {
      const newValue = target.value.slice(0, start) + replacement + target.value.slice(end);
      this.renderer.setProperty(target, 'value', newValue);
      if (this.control && this.control.control) {
        this.control.control.setValue(newValue);
      }
      const cursorPos = start + replacement.length;
      target.setSelectionRange(cursorPos, cursorPos);
    }
  }
  
  checkForUndoTransformation(event: InputEvent){
    let target = event.target as HTMLInputElement | HTMLTextAreaElement
    if (
      event.inputType == 'deleteContentBackward'
      && this.isAfterTransform
      && this.lastOriginalText
      && this.lastReplacementText
      && target.selectionStart == target.selectionEnd
      && target.selectionStart == this.lastReplaceCursorPos - 1
    ){
      const target = event.target as HTMLInputElement | HTMLTextAreaElement
      const len = this.lastReplacementText.length - 1;
      const cursorPos = target.selectionStart ?? 0;
      this.replaceTextBackwards(target, this.lastOriginalText, len, cursorPos);
    }
    this.isAfterTransform = false;
    this.lastOriginalText = null;
    this.lastReplacementText = null;
  }
  
  curlyQuotesTransform(event: InputEvent) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement
    
    if (event.inputType == "insertText") {
      const cursorPos = target.selectionStart ?? 0;
      const prevChar = target.value[cursorPos - 2] ?? '';
      const isPrevEmpty = !prevChar.trim();
      
      if (event.data == '"' && this.lastKeyPressed == '"') {
        if (isPrevEmpty) {
          this.replaceTextBackwards(target, '“', 1, cursorPos);
        } else if (prevChar == '\\') {
          this.replaceTextBackwards(target, '"', 2, cursorPos);
        } else {
          this.replaceTextBackwards(target, '”', 1, cursorPos);
        }
      } else if (event.data == "'" && this.lastKeyPressed == "'") {
        if (isPrevEmpty) {
          this.replaceTextBackwards(target, '‘', 1, cursorPos);
        } else if (prevChar == '\\') {
          this.replaceTextBackwards(target, "'", 2, cursorPos);
        } else {
          this.replaceTextBackwards(target, '’', 1, cursorPos);
        }
      }
      
    }
  }

}

function codemirrorChangeToInputEvent(instance: any, change: any): any|undefined {
  if (change.origin == '+input') {
    return {
      inputType: "insertText",
      data: change.text[0],
      target: {
        value: instance.doc.getValue(),
        selectionStart: instance.doc.indexFromPos(change.from) + 1,
      }
    }
  }
  if (change.origin == "+delete") {
    return {
      inputType: "deleteContentBackward",
      target: {
        value: instance.doc.getValue(),
        selectionStart: instance.doc.indexFromPos(change.from),
        selectionEnd: instance.doc.indexFromPos(change.from),
      }
    }
  }
  return undefined;
}